import moment from 'moment-timezone';
export const convertToUTC = (plainDate, plainTime, timeZoneId) => {
    if (!plainDate || !timeZoneId) {
        return '';
    }
    // Convert "UTC-11:00" to a proper offset format for moment
    const offsetMatch = timeZoneId.match(/UTC([+-]\d{2}):(\d{2})/);
    if (!offsetMatch) {
        throw new Error('Invalid time zone format. Expected format: UTC±HH:MM');
    }
    const offsetHours = parseInt(offsetMatch[1], 10); // Extract ±HH
    const offsetMinutes = parseInt(offsetMatch[2], 10); // Extract MM
    const totalOffsetMinutes = offsetHours * 60 + (offsetHours < 0 ? -offsetMinutes : offsetMinutes); // Compute total minutes
    // Construct a datetime string using moment-timezone
    const dateTime = moment.tz({
        year: plainDate.year,
        month: plainDate.month - 1, // moment uses 0-based months
        day: plainDate.day,
        hour: plainTime?.hour ?? 0,
        minute: plainTime?.minute ?? 0,
        second: plainTime?.second ?? 0,
        millisecond: plainTime?.millisecond ?? 0,
    }, 'UTC');
    // Convert to ISO format
    dateTime.subtract(totalOffsetMinutes, 'minutes');
    // Convert to UTC and return as ISO string
    return dateTime.toISOString();
};
export const validateJSONFile = async (file) => {
    return new Promise((resolve, reject) => {
        // Check if the file type is JSON
        if (!file || file.type !== 'application/json') {
            reject(new Error('Invalid file type. Please upload a JSON file.'));
            return;
        }
        const reader = new FileReader();
        // Handle successful file reading
        reader.onload = (event) => {
            try {
                // Parse the file content to check if it's valid JSON
                if (event.target && typeof event.target.result === 'string') {
                    const parsedJSON = JSON.parse(event.target.result);
                    resolve(parsedJSON);
                }
                else {
                    reject(new Error('The file content is not valid JSON.'));
                }
            }
            catch {
                reject(new Error('The file content is not valid JSON.'));
            }
        };
        // Handle file reading errors
        reader.onerror = () => {
            reject(new Error('An error occurred while reading the file.'));
        };
        // Start reading the file as text
        reader.readAsText(file);
    });
};
export const getFormattedNumber = (number, locale) => {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'decimal',
        notation: 'compact',
        compactDisplay: 'short',
    });
    return formatter.format(number).toLowerCase();
};
export const validatePattern = (input, allowedKeywords = [], hasExtension = false) => {
    // Create a regex to match the allowed keywords separated by dashes
    const keywordsRegex = `(?:${allowedKeywords.map((k) => k.replace(/[{}]/g, '$&')).join('|')})`;
    // Regex to match the entire format with optional file extension
    let fullRegex;
    if (hasExtension) {
        fullRegex = new RegExp(`^(${keywordsRegex})([ -](${keywordsRegex}))*((.[a-zA-Z0-9]+)?)$`);
    }
    else {
        fullRegex = new RegExp(`^(${keywordsRegex})([ -](${keywordsRegex}))*$`);
    }
    // Validate the input string against the regex
    return fullRegex.test(input);
};
export const validateFileNamePattern = (input) => {
    const allowedKeywords = ['ENV', 'PROTOCOL', 'STUDY', 'TASK', 'CAT', 'YYYY', 'MM', 'DD', 'hh', 'mm'];
    // Disallow "/" in the input
    if (input.includes('/')) {
        return false;
    }
    // Find all valid patterns like {keyword}
    const validPatterns = input.match(/\{([^}]*)\}/g) || [];
    // Check each pattern to ensure it contains only allowed keywords
    for (const pattern of validPatterns) {
        const content = pattern.slice(1, -1); // Remove { and }
        // Empty patterns {} or invalid keywords are not valid
        if (content === '' || !allowedKeywords.includes(content)) {
            return false;
        }
    }
    // Remove all valid patterns from the input
    let remaining = input;
    for (const pattern of validPatterns) {
        remaining = remaining.replace(pattern, '');
    }
    // Check if any brackets remain - if yes, they're invalid
    if (remaining.includes('{') || remaining.includes('}')) {
        return false;
    }
    return true;
};
export const validateTimeFormat = (input) => {
    const allowedKeywords = ['s', 'ss', 'm', 'mm', 'h', 'hh', 'H', 'HH'];
    return validatePattern(input, allowedKeywords);
};
export const validateDateFormat = (input) => {
    const allowedKeywords = ['d', 'dd', 'ddd', 'dddd', 'DD', 'M', 'MM', 'MMM', 'MMMM', 'YY', 'YYYY'];
    return validatePattern(input, allowedKeywords);
};
export const validateDateTimeFormat = (input) => {
    const allowedKeywords = [
        'd',
        'dd',
        'ddd',
        'dddd',
        'DD',
        'M',
        'MM',
        'MMM',
        'MMMM',
        'YY',
        'YYYY',
        's',
        'ss',
        'm',
        'mm',
        'h',
        'hh',
        'H',
        'HH',
    ];
    return validatePattern(input, allowedKeywords);
};
export const isValidCommaSeparatedStrings = (input) => {
    // A regular expression to match a comma-separated list of non-empty strings
    const regex = /^\s*[^,]+(?:\s*,\s*[^,]+)*\s*$/;
    return regex.test(input);
};
